/**
 * Minimal vCard 3.0/4.0 parser for display.
 * Returns first contact's main fields.
 */
export type ParsedVCard = {
  name: string
  tel: string[]
  /** Numbers with TYPE=CELL (mobile). */
  mobileTel: string[]
  email: string[]
  org: string
  title: string
  impp: string[]
}

const unescape = (s: string): string =>
  s.replace(/\\,/g, ',').replace(/\\;/g, ';').replace(/\\\\/g, '\\')

/** Checks if TEL line has TYPE=CELL (mobile). */
const isTelTypeCell = (keyPart: string): boolean =>
  /TYPE=CELL\b/i.test(keyPart) || /;CELL\b/i.test(keyPart)

export const parseVCard = (vcardText: string): ParsedVCard | null => {
  if (!vcardText.includes('BEGIN:VCARD')) {
    return null
  }
  const result: ParsedVCard = {
    name: '',
    tel: [],
    mobileTel: [],
    email: [],
    org: '',
    title: '',
    impp: [],
  }
  const lines = vcardText.split(/\r?\n/)
  for (let i = 0; i < lines.length; i++) {
    let line = lines[i]
    while (i + 1 < lines.length && lines[i + 1]?.startsWith(' ') && !lines[i + 1]?.trim().startsWith('BEGIN:')) {
      line += lines[++i]?.slice(1) ?? ''
    }
    const colon = line?.indexOf(':')
    if (colon === -1 || colon === undefined) continue
    const value = line?.slice(colon + 1).trim() ?? ''
    const keyPart = line?.slice(0, colon) ?? ''
    const key = keyPart.toUpperCase().split(';')[0]
    if (key === 'FN') {
      result.name = unescape(value) || result.name
    } else if (key === 'TEL') {
      if (value) {
        if (isTelTypeCell(keyPart)) {
          result.mobileTel.push(value)
        } else {
          result.tel.push(value)
        }
      }
    } else if (key === 'EMAIL') {
      if (value) result.email.push(value)
    } else if (key === 'IMPP') {
      if (value) result.impp.push(unescape(value))
    } else if (key === 'ORG') {
      result.org = unescape(value.replace(/;/g, ', '))
    } else if (key === 'TITLE') {
      result.title = unescape(value)
    } else if (key === 'END' && value === 'VCARD' && result.name) {
      return result
    }
  }
  return result.name ? result : null
}

export const normalizeImppToPServed = (value: string): string | undefined => {
  const trimmed = value.trim()
  if (!trimmed) {
    return undefined
  }

  if (trimmed.startsWith('<sip:')) {
    return trimmed
  }

  const withoutScheme = trimmed.replace(/^sip:/i, '')
  if (trimmed.toLowerCase().startsWith('sip:') || withoutScheme.includes('@')) {
    return `<sip:${withoutScheme.replace(/^sip:/i, '')}>`
  }

  return trimmed.startsWith('<') ? trimmed : undefined
}
